// src/components/generic-crud/GenericFiltersResult.js
import PropTypes from 'prop-types';
import { Box, Chip, Button, Stack } from '@mui/material';
import Iconify from 'src/components/iconify';

export default function GenericFiltersResult({
  config,
  filters,
  onFilters,
  onResetFilters,
  results,
}) {
  const activeFilters = config.fields.filter((field) => {
    const value = filters[field.name];
    const defaultValue = config.default[field.name];

    if (field.type === 'dateRange') {
      return value && (value.start || value.end);
    }

    if (field.type === 'numberRange') {
      return value && (value.min !== '' || value.max !== '');
    }

    if (typeof value === 'object' && value !== null) {
        // Simple comparison for other objects
        return JSON.stringify(value) !== JSON.stringify(defaultValue);
    }
    
    return value && value !== 'all' && value !== defaultValue;
  });

  if (activeFilters.length === 0) return null;

  const handleRemoveFilter = (fieldName) => {
    onFilters(fieldName, config.default[fieldName]);
  };

  return (
    <Stack spacing={1.5} sx={{ p: 2.5, pt: 0 }}>
      <Box sx={{ typography: 'body2' }}>
        <strong>{results}</strong>
        <Box component="span" sx={{ color: 'text.secondary', ml: 0.25 }}>
          results found
        </Box>
      </Box>

      <Stack flexGrow={1} spacing={1} direction="row" flexWrap="wrap" alignItems="center">
        {(() => {
          // Check if we have both startDate and endDate to combine them
          const hasStartDate = filters.startDate && filters.startDate !== config.default.startDate;
          const hasEndDate = filters.endDate && filters.endDate !== config.default.endDate;
          const dateFieldsToSkip = new Set();

          // Helper to format date nicely
          const formatDate = (date) => {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
          };

          // If both dates exist, we'll show them as one chip
          const chips = [];

          if (hasStartDate && hasEndDate) {
            const startStr = formatDate(filters.startDate);
            const endStr = formatDate(filters.endDate);
            const isSameDay = startStr === endStr;

            chips.push(
              <Chip
                key="dateRange"
                label={isSameDay ? `Date: ${startStr}` : `Date: ${startStr} - ${endStr}`}
                size="small"
                onDelete={() => {
                  onFilters('startDate', config.default.startDate);
                  onFilters('endDate', config.default.endDate);
                }}
              />
            );
            dateFieldsToSkip.add('startDate');
            dateFieldsToSkip.add('endDate');
          }

          // Add all other active filters
          activeFilters.forEach((field) => {
            // Skip if we already handled this as part of date range
            if (dateFieldsToSkip.has(field.name)) return;

            let label = `${field.label}: `;
            const value = filters[field.name];

            if (field.type === 'select' || field.type === 'tabs') {
              const option = field.options.find((opt) => opt.value === value);
              label += option?.label || value;
            } else if (field.type === 'autocomplete') {
              if (Array.isArray(value)) {
                label += value
                  .map((v) => (typeof v === 'object' && v !== null ? (v[field.optionLabel] || v.label || v.name || v.title || 'Unknown') : v))
                  .join(', ');
              } else if (typeof value === 'object' && value !== null) {
                label += value[field.optionLabel] || value.label || value.name || value.title || value;
              } else {
                label += value;
              }
            } else if (field.type === 'dateRange') {
              const start = value.start ? formatDate(value.start) : '...';
              const end = value.end ? formatDate(value.end) : '...';
              label += `${start} - ${end}`;
            } else if (field.type === 'numberRange') {
              const min = (value.min !== undefined && value.min !== '') ? value.min : '...';
              const max = (value.max !== undefined && value.max !== '') ? value.max : '...';
              label += `${min} - ${max}`;
            } else if (field.type === 'date') {
              // Individual date field (not combined)
              label += formatDate(value);
            } else {
              label += value;
            }

            chips.push(
              <Chip
                key={field.name}
                label={label}
                size="small"
                onDelete={() => handleRemoveFilter(field.name)}
              />
            );
          });

          return chips;
        })()}

        <Button
          color="error"
          onClick={onResetFilters}
          startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
        >
          Clear
        </Button>
      </Stack>
    </Stack>
  );
}

GenericFiltersResult.propTypes = {
  config: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  onFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  results: PropTypes.number.isRequired,
};
